import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import html2canvas from 'html2canvas';
import { jsPDF } from 'jspdf';
import { useVocabulary } from './contexts/VocabularyContext';
import { VocabularyTabs, StudentSelector } from './components/VocabularyTabs';
import './css/Vocabulary.css';
import './css/VocabularyQuiz.css';

/**
 * 오답 노트 — 이 앱의 핵심 화면.
 * 틀린 단어를 쌓아 두었다가, 기간을 정해 몰아서 다시 시험 본다.
 */
const VocabularyReview = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { quizApi, studentId } = useVocabulary();

  // 기간은 사용자가 직접 지정한다 (비워 두면 전체 기간)
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [onlyUnresolved, setOnlyUnresolved] = useState(true);
  const [notes, setNotes] = useState([]);
  const [total, setTotal] = useState(0);
  const [weakness, setWeakness] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [count, setCount] = useState(20);
  const [format, setFormat] = useState('multiple_choice');
  const [direction, setDirection] = useState('word_to_meaning');
  const [revealed, setRevealed] = useState([]);
  // 오답 AI 분석 (사용자가 누를 때만 실행 — 호출마다 과금된다)
  const [analysis, setAnalysis] = useState(null);
  const [analyzing, setAnalyzing] = useState(false);
  // 저장된 분석 이력. 화면을 나갔다 와도 다시 볼 수 있어야 한다
  const [history, setHistory] = useState([]);
  const [exporting, setExporting] = useState(false);
  const reportRef = useRef(null);

  const targetUserId = studentId || undefined;

  const load = useCallback(async () => {
    setLoading(true);
    const params = {};
    if (from) params.from = from;
    if (to) params.to = to;
    if (onlyUnresolved) params.onlyUnresolved = '1';

    const [notesData, weaknessData] = await Promise.all([
      quizApi.getWrongNotes({ ...params, limit: 200 }, targetUserId),
      quizApi.getWeakness(from || to ? { from, to } : {}, targetUserId),
    ]);

    if (notesData.success) {
      setNotes(notesData.words || []);
      setTotal(notesData.total || 0);
    }
    if (weaknessData.success) setWeakness(weaknessData);
    setRevealed([]);
    setLoading(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [from, to, onlyUnresolved, targetUserId]);

  useEffect(() => { load(); }, [load]);

  const handleReviewQuiz = async () => {
    if (notes.length === 0) return;
    setBusy(true);
    try {
      const data = await quizApi.createReviewQuiz({
        from: from || undefined,
        to: to || undefined,
        question_count: Number(count),
        answer_format: format,
        direction,
        only_unresolved: onlyUnresolved,
        title: from || to
          ? t('vocabulary.review.quizTitleRange', { from: from || '~', to: to || '~' })
          : t('vocabulary.review.quizTitleAll'),
      }, targetUserId);

      if (!data.success) {
        toast.error(t('vocabulary.review.createFailed'));
        return;
      }
      if (data.wrong_note_total > data.question_count) {
        toast.info(t('vocabulary.review.partialCoverage', {
          count: data.question_count, total: data.wrong_note_total,
        }));
      }
      navigate(`/vocabulary/quiz/${data.quiz_id}`);
    } finally {
      setBusy(false);
    }
  };

  const toggleReveal = (wordId) => {
    setRevealed(prev => prev.includes(wordId) ? prev.filter(id => id !== wordId) : [...prev, wordId]);
  };

  const axisLabel = (axis, key) => {
    const map = {
      by_direction: { word_to_meaning: t('vocabulary.quiz.wordToMeaning'), meaning_to_word: t('vocabulary.quiz.meaningToWord') },
      by_format: { multiple_choice: t('vocabulary.quiz.multipleChoice'), short_answer: t('vocabulary.quiz.shortAnswer') },
    };
    return map[axis]?.[key] || key;
  };

  /*
   * 분석 이력을 불러온다.
   * 분석은 과금되므로 화면에 들어올 때마다 다시 돌리지 않고, 저장된 것을 보여준다.
   * 가장 최근 것을 자동으로 펼쳐 둔다.
   */
  const loadHistory = useCallback(async (openLatest = false) => {
    const data = await quizApi.listAnalyses({ limit: 20 }, targetUserId);
    if (!data.success) return;
    const list = data.analyses || [];
    setHistory(list);
    if (openLatest && list.length > 0) {
      const latest = await quizApi.getAnalysis(list[0].analysis_id, targetUserId);
      if (latest.success) setAnalysis(latest);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [targetUserId]);

  useEffect(() => {
    setAnalysis(null);
    loadHistory(true);
  }, [loadHistory]);

  const openAnalysis = async (analysisId) => {
    const data = await quizApi.getAnalysis(analysisId, targetUserId);
    if (data.success) setAnalysis(data);
  };

  const removeAnalysis = async (analysisId) => {
    if (!window.confirm(t('vocabulary.review.deleteAnalysisConfirm'))) return;
    const data = await quizApi.deleteAnalysis(analysisId, targetUserId);
    if (!data.success) {
      toast.error(t('error.save'));
      return;
    }
    setAnalysis(null);
    loadHistory(false);
  };

  const rangeLabel = (fromDate, toDate) => {
    if (!fromDate && !toDate) return t('vocabulary.review.allPeriod');
    return `${fromDate || t('vocabulary.review.beginning')} ~ ${toDate || t('vocabulary.review.today')}`;
  };

  // 로컬 기준 표기. toLocaleString은 이 앱의 언어 코드(kr/ch/jp)가 BCP-47이 아니라 쓰지 않는다
  const formatDateTime = (value) => {
    if (!value) return '';
    const d = new Date(value);
    if (isNaN(d.getTime())) return '';
    const p = (n) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())} ${p(d.getHours())}:${p(d.getMinutes())}`;
  };

  const severityLabel = (sev) => t(`vocabulary.review.severity.${sev || 'medium'}`);

  /**
   * 리포트를 PDF로 저장한다.
   *
   * 한 번 캡처한 뒤 A4 높이로 잘라 여러 장에 나눈다. 자를 위치는 섹션 경계로 맞춘다 —
   * 문단 중간에서 끊기면 읽기 힘들다.
   * 다크 모드 그대로 뽑으면 검은 배경에 흰 글씨가 되어 인쇄가 불가능하므로,
   * 캡처하는 동안만 인쇄용 색으로 바꾼다.
   */
  const exportPdf = async () => {
    const node = reportRef.current;
    if (!node || exporting) return;
    setExporting(true);
    node.classList.add('report-exporting');

    try {
      const CAPTURE_SCALE = 2;
      const nodeTop = node.getBoundingClientRect().top;
      const breaks = Array.from(node.querySelectorAll('section'))
        .map(el => (el.getBoundingClientRect().top - nodeTop) * CAPTURE_SCALE);

      const canvas = await html2canvas(node, {
        scale: CAPTURE_SCALE,
        backgroundColor: '#ffffff',
        useCORS: true,
        logging: false,
      });

      const pdf = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
      const pageW = pdf.internal.pageSize.getWidth();
      const pageH = pdf.internal.pageSize.getHeight();
      const pxPerMm = canvas.width / pageW;
      const pageHpx = Math.floor(pageH * pxPerMm);

      let y = 0;
      let page = 0;
      while (y < canvas.height) {
        const top = y;
        let sliceH = Math.min(pageHpx, canvas.height - top);

        // 마지막 장이 아니면 섹션 경계까지 당겨서 자른다.
        // 페이지의 35% 이전으로는 물러나지 않는다 (한 섹션이 한 장보다 길면 무한 루프가 된다)
        if (top + sliceH < canvas.height) {
          const lower = top + pageHpx * 0.35;
          const upper = top + sliceH;
          const fit = breaks.filter(b => b > lower && b <= upper);
          if (fit.length > 0) sliceH = Math.max(...fit) - top;
        }

        const slice = document.createElement('canvas');
        slice.width = canvas.width;
        slice.height = sliceH;
        const ctx = slice.getContext('2d');
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(0, 0, slice.width, slice.height);
        ctx.drawImage(canvas, 0, -top);

        if (page > 0) pdf.addPage();
        pdf.addImage(slice.toDataURL('image/png'), 'PNG', 0, 0, pageW, sliceH / pxPerMm);

        y = top + sliceH;
        page++;
      }

      const range = analysis.from || analysis.to
        ? `${analysis.from || ''}_${analysis.to || ''}`
        : t('vocabulary.review.allPeriod');
      pdf.save(`${t('vocabulary.review.analysisTitle')}_${range}.pdf`.replace(/[^\w가-힣.~-]+/g, '_'));
    } catch (e) {
      console.error('Analysis PDF export failed:', e);
      toast.error(t('vocabulary.quiz.pdfFailed'));
    } finally {
      node.classList.remove('report-exporting');
      setExporting(false);
    }
  };

  const runAnalysis = async () => {
    setAnalyzing(true);
    try {
      const data = await quizApi.analyzeWrongAnswers(
        { from: from || undefined, to: to || undefined },
        targetUserId
      );
      if (!data.success) {
        /*
         * 원인별로 다르게 알린다.
         * 키 문제인데 '다시 시도하세요'라고 하면 눌러도 계속 같은 실패만 겪는다.
         */
        const messageByReason = {
          'AI analysis is not available': 'analysisUnavailable',
          'AI api key is invalid': 'analysisKeyInvalid',
          'AI quota exceeded': 'analysisQuota',
          'No wrong answers to analyze': 'analysisNoData',
          'AI response truncated': 'analysisTruncated',
        };
        toast.error(t(`vocabulary.review.${messageByReason[data.message] || 'analysisFailed'}`));
        return;
      }
      setAnalysis(data);
      // 새 분석이 이력 맨 앞에 오도록 목록을 갱신한다
      loadHistory(false);
    } finally {
      setAnalyzing(false);
    }
  };

  const renderAxis = (axis, title) => {
    const rows = weakness?.[axis] || [];
    if (rows.length === 0) return null;
    return (
      <div className="weak-axis">
        <h4>{title}</h4>
        <ul>
          {rows.slice(0, 5).map(r => (
            <li key={r.key}>
              <span className="weak-key">{axisLabel(axis, r.key)}</span>
              <span className="weak-bar">
                <span className="weak-bar-fill" style={{ width: `${r.wrong_rate * 100}%` }} />
              </span>
              <span className="weak-rate">{Math.round(r.wrong_rate * 100)}%</span>
            </li>
          ))}
        </ul>
      </div>
    );
  };

  return (
    <div className="vocab-container">
      <div className="vocab-header">
        <h2>{t('vocabulary.review.title')}</h2>
        <div className="vocab-header-actions">
          <StudentSelector />
        </div>
      </div>

      <VocabularyTabs />

      {/* 기간 지정 */}
      <div className="review-period">
        <label className="quiz-field">
          <span>{t('vocabulary.review.from')}</span>
          <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} />
        </label>
        <label className="quiz-field">
          <span>{t('vocabulary.review.to')}</span>
          <input type="date" value={to} onChange={(e) => setTo(e.target.value)} />
        </label>
        <button className="vocab-btn" onClick={() => { setFrom(''); setTo(''); }}>
          {t('vocabulary.review.allPeriod')}
        </button>
        <label className="vocab-checkbox">
          <input type="checkbox" checked={onlyUnresolved} onChange={(e) => setOnlyUnresolved(e.target.checked)} />
          {t('vocabulary.review.onlyUnresolved')}
        </label>
      </div>

      <p className="review-count">
        {t('vocabulary.review.summary', { count: total })}
        {(from || to) && (
          <em> · {from || t('vocabulary.review.beginning')} ~ {to || t('vocabulary.review.today')}</em>
        )}
      </p>

      {/* 기간 오답 시험 */}
      {notes.length > 0 && (
        <div className="review-quiz-box">
          <div className="review-quiz-options">
            <label className="quiz-field">
              <span>{t('vocabulary.quiz.questionCount')}</span>
              <input type="number" min="1" max="100" value={count} onChange={(e) => setCount(e.target.value)} />
            </label>
            <label className="quiz-field">
              <span>{t('vocabulary.quiz.direction')}</span>
              <select value={direction} onChange={(e) => setDirection(e.target.value)}>
                <option value="word_to_meaning">{t('vocabulary.quiz.wordToMeaning')}</option>
                <option value="meaning_to_word">{t('vocabulary.quiz.meaningToWord')}</option>
                <option value="mixed">{t('vocabulary.quiz.mixed')}</option>
              </select>
            </label>
            <label className="quiz-field">
              <span>{t('vocabulary.quiz.format')}</span>
              <select value={format} onChange={(e) => setFormat(e.target.value)}>
                <option value="multiple_choice">{t('vocabulary.quiz.multipleChoice')}</option>
                <option value="short_answer">{t('vocabulary.quiz.shortAnswer')}</option>
                <option value="mixed">{t('vocabulary.quiz.mixed')}</option>
              </select>
            </label>
          </div>
          <button className="vocab-btn vocab-btn-primary" onClick={handleReviewQuiz} disabled={busy}>
            {busy ? t('vocabulary.loading') : t('vocabulary.review.startQuiz')}
          </button>
        </div>
      )}

      <div className="review-body">
        {/* 오답 단어 목록 — 뜻을 가려 두어 암기 확인용으로도 쓸 수 있다 */}
        <div className="review-notes">
          {loading && <p className="vocab-empty">{t('vocabulary.loading')}</p>}
          {!loading && notes.length === 0 && (
            <p className="vocab-empty">{t('vocabulary.review.noWrongAnswers')}</p>
          )}
          <ul className="review-list">
            {notes.map(n => (
              <li key={n.word_id} className={`review-item${n.resolved ? ' resolved' : ''}`}>
                <div className="review-item-head">
                  <span className="review-word">
                    {n.is_important && <em className="review-star">★</em>}
                    {n.word}
                  </span>
                  <span className="review-stats">
                    {t('vocabulary.review.wrongOf', { wrong: n.wrong_count, attempts: n.attempts })}
                    <em className="review-rate">{Math.round(n.wrong_rate * 100)}%</em>
                  </span>
                </div>

                <button className="review-meaning" onClick={() => toggleReveal(n.word_id)}>
                  {revealed.includes(n.word_id)
                    ? n.meaning
                    : <span className="review-hidden">{t('vocabulary.review.tapToReveal')}</span>}
                </button>

                <div className="review-item-foot">
                  {n.study_date && <span className="review-date">{n.study_date}</span>}
                  {n.resolved && <span className="review-badge ok">{t('vocabulary.review.resolved')}</span>}
                  {n.deleted_from_vocabulary && (
                    <span className="review-badge gone">{t('vocabulary.review.deletedWord')}</span>
                  )}
                </div>
              </li>
            ))}
          </ul>
        </div>

        {/* 약점 분석 */}
        {weakness && weakness.overall.attempts > 0 && (
          <aside className="review-weakness">
            <h3>{t('vocabulary.review.weakness')}</h3>
            <p className="weak-overall">
              {t('vocabulary.review.overallRate', {
                rate: Math.round(weakness.overall.wrong_rate * 100),
                attempts: weakness.overall.attempts,
              })}
            </p>
            {renderAxis('by_date', t('vocabulary.review.byDate'))}
            {renderAxis('by_direction', t('vocabulary.review.byDirection'))}
            {renderAxis('by_format', t('vocabulary.review.byFormat'))}
            {renderAxis('by_part_of_speech', t('vocabulary.review.byPos'))}

            {/*
              * 통계는 무엇을 틀렸는지까지만 알려준다.
              * 왜 틀렸는지는 오답 하나하나를 봐야 알 수 있어 AI에 맡긴다.
              * 호출마다 과금되므로 사용자가 눌렀을 때만 실행한다.
              */}
            <div className="review-ai">
              <button
                className="vocab-btn vocab-btn-primary review-ai-btn"
                onClick={runAnalysis}
                disabled={analyzing}
              >
                {analyzing ? t('vocabulary.review.analyzing') : t('vocabulary.review.runAnalysis')}
              </button>
              <p className="quiz-hint">{t('vocabulary.review.analysisHint')}</p>
            </div>
          </aside>
        )}
      </div>

      {/* 분석 이력 — 기간별로 쌓인 것을 골라 다시 본다 */}
      {history.length > 0 && (
        <div className="report-history">
          <span className="report-history-label">{t('vocabulary.review.pastAnalyses')}</span>
          {history.map(h => (
            <button
              key={h.analysis_id}
              className={`report-history-chip${analysis?.analysis_id === h.analysis_id ? ' active' : ''}`}
              onClick={() => openAnalysis(h.analysis_id)}
            >
              <strong>{rangeLabel(h.from, h.to)}</strong>
              <em>{formatDateTime(h.created_at)} · {h.analyzed_count}{t('vocabulary.quiz.questionsSuffix')}</em>
            </button>
          ))}
        </div>
      )}

      {analysis && (
        <article className="report" ref={reportRef}>
          <header className="report-head">
            <div className="report-title">
              <h3>{t('vocabulary.review.analysisTitle')}</h3>
              <span className="report-range">{rangeLabel(analysis.from, analysis.to)}</span>
            </div>
            <div className="report-facts">
              <span>{t('vocabulary.review.analysisMeta', { count: analysis.analyzed_count })}</span>
              <span>{formatDateTime(analysis.created_at)}</span>
              {analysis.model && <span className="report-model">{analysis.model}</span>}
              {/* PDF 버튼은 캡처 대상에서 빼야 결과물에 버튼이 찍히지 않는다 */}
              <button className="vocab-btn report-pdf" onClick={exportPdf} disabled={exporting}>
                {exporting ? t('vocabulary.loading') : t('vocabulary.review.savePdf')}
              </button>
              {analysis.analysis_id && (
                <button
                  className="report-delete"
                  onClick={() => removeAnalysis(analysis.analysis_id)}
                  title={t('delete')}
                >
                  &#128465;
                </button>
              )}
            </div>
          </header>

          {analysis.analysis.summary && (
            <section className="report-lead">
              <h4 className="report-section-label">{t('vocabulary.review.reportSummary')}</h4>
              <p>{analysis.analysis.summary}</p>
            </section>
          )}

          {analysis.analysis.patterns.length > 0 && (
            <section className="report-section">
              <h4 className="report-section-label">
                <span className="report-num">1</span>{t('vocabulary.review.patterns')}
              </h4>
              <div className="report-patterns">
                {analysis.analysis.patterns.map((p, i) => (
                  <div key={i} className={`report-pattern sev-${p.severity || 'medium'}`}>
                    <div className="report-pattern-top">
                      <span className="report-sev">{severityLabel(p.severity)}</span>
                      <strong>{p.title}</strong>
                      {p.type && <span className="report-tag">{p.type}</span>}
                    </div>
                    <p>{p.explanation}</p>
                    {Array.isArray(p.evidence) && p.evidence.length > 0 && (
                      <div className="report-evidence">
                        {p.evidence.map((w, j) => <span key={j}>{w}</span>)}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </section>
          )}

          {analysis.analysis.confusion_pairs.length > 0 && (
            <section className="report-section">
              <h4 className="report-section-label">
                <span className="report-num">2</span>{t('vocabulary.review.confusionPairs')}
              </h4>
              <table className="report-table">
                <thead>
                  <tr>
                    <th>{t('vocabulary.quiz.correctAnswer')}</th>
                    <th>{t('vocabulary.review.confusedWith')}</th>
                    <th>{t('vocabulary.review.confusedReason')}</th>
                  </tr>
                </thead>
                <tbody>
                  {analysis.analysis.confusion_pairs.map((c, i) => (
                    <tr key={i}>
                      <td className="report-word">{c.word}</td>
                      <td className="report-word wrong">{c.confused_with}</td>
                      <td className="report-reason">{c.reason}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </section>
          )}

          {analysis.analysis.study_plan.length > 0 && (
            <section className="report-section">
              <h4 className="report-section-label">
                <span className="report-num">3</span>{t('vocabulary.review.studyPlan')}
              </h4>
              <ol className="report-plan">
                {analysis.analysis.study_plan.map((s, i) => (
                  <li key={i}>
                    <strong>{s.focus}</strong>
                    <p>{s.action}</p>
                    {s.why && <span className="report-plan-why">{s.why}</span>}
                  </li>
                ))}
              </ol>
            </section>
          )}

          {analysis.analysis.priority_words.length > 0 && (
            <section className="report-section">
              <h4 className="report-section-label">
                <span className="report-num">4</span>{t('vocabulary.review.priorityWords')}
              </h4>
              <div className="report-words">
                {analysis.analysis.priority_words.map((w, i) => <span key={i}>{w}</span>)}
              </div>
            </section>
          )}
        </article>
      )}
    </div>
  );
};

export default VocabularyReview;
